import type {
  ItineraryCanvasserRowDto,
  ItineraryAssignmentDto,
  ItineraryTurfPoolDto,
  ItineraryAssignmentUpsert,
  AvailabilityChange,
} from '../types/campaign'

export interface EditorState {
  canvassers: ItineraryCanvasserRowDto[]
  pendingUpserts: Map<string, ItineraryAssignmentUpsert>
  pendingDeletions: Set<string>
  pendingAvailability: Map<string, AvailabilityChange>
}

export const EMPTY_EDITOR_STATE: EditorState = {
  canvassers: [],
  pendingUpserts: new Map(),
  pendingDeletions: new Set(),
  pendingAvailability: new Map(),
}

// L11 slice 1f: itinerary canvasser rows are keyed on the canvasser's ACTIVE grant id (not a
// User id), which round-trips through the assignment write gate. Actions carry that grant id.
export type EditorAction =
  | { type: 'sync'; payload: ItineraryCanvasserRowDto[] }
  | { type: 'assign'; turf: ItineraryTurfPoolDto; grantId: string; dateStr: string }
  | { type: 'move'; assignment: ItineraryAssignmentDto; targetGrantId: string; targetDateStr: string }
  | { type: 'remove'; assignmentId: string }
  | { type: 'toggleAvailability'; grantId: string; dateStr: string; isAvailable: boolean }

function findLocation(canvassers: ItineraryCanvasserRowDto[], assignmentId: string): { grantId: string; dateStr: string } | null {
  for (const c of canvassers) {
    for (const [dateStr, assignments] of Object.entries(c.dayAssignments)) {
      if (assignments.some(a => a.id === assignmentId)) return { grantId: c.grantId, dateStr }
    }
  }
  return null
}

export function itineraryEditorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'sync':
      return { canvassers: action.payload, pendingUpserts: new Map(), pendingDeletions: new Set(), pendingAvailability: new Map() }

    case 'assign': {
      const { turf, grantId, dateStr } = action
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const existingLen = state.canvassers.find(c => c.grantId === grantId)?.dayAssignments[dateStr]?.length ?? 0
      const newAssignment: ItineraryAssignmentDto = {
        id: tempId, itineraryDayId: '', scheduledDate: dateStr, turfId: turf.turfId, turfLabel: turf.turfLabel,
        turfIndex: turf.turfIndex, turfMapLabel: turf.turfMapLabel, assignedGrantId: grantId,
        assignedCanvasserName: state.canvassers.find(c => c.grantId === grantId)?.fullName ?? null,
        sortOrder: existingLen, status: 'Scheduled', passNumber: 1, doorsKnocked: 0, contactsMade: 0,
        turfMemberCount: turf.memberCount, turfVisitedCount: turf.visitedCount, notes: null,
        purpose: turf.purpose, followUpKind: turf.followUpKind, followUpCustomKindName: turf.followUpCustomKindName,
      }
      const canvassers = state.canvassers.map(c => {
        if (c.grantId !== grantId) return c
        const dayAssignments = { ...c.dayAssignments }
        dayAssignments[dateStr] = [...(dayAssignments[dateStr] ?? []), newAssignment]
        return { ...c, dayAssignments }
      })
      const pendingUpserts = new Map(state.pendingUpserts)
      pendingUpserts.set(tempId, { scheduledDate: dateStr, turfId: turf.turfId, assignedGrantId: grantId, sortOrder: existingLen })
      return { ...state, canvassers, pendingUpserts }
    }

    case 'move': {
      const { assignment, targetGrantId, targetDateStr } = action
      const src = findLocation(state.canvassers, assignment.id)
      if (!src) return state
      if (src.grantId === targetGrantId && src.dateStr === targetDateStr) return state

      const canvassers = state.canvassers.map(c => ({ ...c, dayAssignments: { ...c.dayAssignments } }))
      const sourceCanvasser = canvassers.find(c => c.grantId === src.grantId)
      if (sourceCanvasser?.dayAssignments[src.dateStr]) {
        sourceCanvasser.dayAssignments[src.dateStr] = sourceCanvasser.dayAssignments[src.dateStr].filter(a => a.id !== assignment.id)
      }
      const targetCanvasser = canvassers.find(c => c.grantId === targetGrantId)
      if (targetCanvasser) {
        const existing = targetCanvasser.dayAssignments[targetDateStr] ?? []
        targetCanvasser.dayAssignments[targetDateStr] = [
          ...existing,
          { ...assignment, scheduledDate: targetDateStr, assignedGrantId: targetGrantId, assignedCanvasserName: targetCanvasser.fullName, sortOrder: existing.length },
        ]
      }
      const pendingUpserts = new Map(state.pendingUpserts)
      pendingUpserts.set(assignment.id, {
        id: assignment.id.startsWith('temp-') ? undefined : assignment.id,
        scheduledDate: targetDateStr, turfId: assignment.turfId, assignedGrantId: targetGrantId,
        sortOrder: 0, notes: assignment.notes ?? undefined,
      })
      return { ...state, canvassers, pendingUpserts }
    }

    case 'remove': {
      const { assignmentId } = action
      const loc = findLocation(state.canvassers, assignmentId)
      if (!loc) return state

      const canvassers = state.canvassers.map(c => {
        if (c.grantId !== loc.grantId) return c
        const dayAssignments = { ...c.dayAssignments }
        dayAssignments[loc.dateStr] = (dayAssignments[loc.dateStr] ?? []).filter(a => a.id !== assignmentId)
        return { ...c, dayAssignments }
      })
      const pendingDeletions = new Set(state.pendingDeletions)
      if (!assignmentId.startsWith('temp-')) pendingDeletions.add(assignmentId)
      const pendingUpserts = new Map(state.pendingUpserts)
      pendingUpserts.delete(assignmentId)
      return { ...state, canvassers, pendingDeletions, pendingUpserts }
    }

    case 'toggleAvailability': {
      const { grantId, dateStr, isAvailable } = action
      const canvassers = state.canvassers.map(c =>
        c.grantId !== grantId ? c : { ...c, availability: { ...c.availability, [dateStr]: isAvailable } }
      )
      const pendingAvailability = new Map(state.pendingAvailability)
      pendingAvailability.set(`${grantId}:${dateStr}`, { grantId, date: dateStr, isAvailable })
      return { ...state, canvassers, pendingAvailability }
    }

    default:
      return state
  }
}
